/**
 * ConversionIntelligence.jsx — Deep AI-powered conversion analysis
 * Features: Overview dashboard + Ad drill-down + Employee drill-down
 * Lazy-loaded (zero performance impact until user clicks "Run Analysis")
 */
import { useState } from "react";
import { analyticsApi } from "@/services/api";
import { toast } from "sonner";
import {
  Brain, TrendingUp, MapPin, UserCheck, Clock, Target,
  ChevronDown, ChevronUp, ArrowRight, Loader2, Filter, Sparkles,
  BarChart3, AlertTriangle, X, DollarSign, Eye, Zap,
  MousePointerClick,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell,
} from "recharts";

const PERIOD_OPTIONS = [
  { value: "last_7_days", label: "7D" },
  { value: "last_30_days", label: "30D" },
  { value: "this_month", label: "This Month" },
  { value: "last_month", label: "Last Month" },
  { value: "all", label: "All" },
];

const PRIORITY_STYLES = {
  HIGH: "border-red-200 bg-red-50/60",
  MEDIUM: "border-amber-200 bg-amber-50/60",
  LOW: "border-blue-200 bg-blue-50/60",
};
const PRIORITY_DOT = { HIGH: "bg-red-500", MEDIUM: "bg-amber-500", LOW: "bg-blue-500" };
const FUNNEL_COLORS = [
  "#3b82f6", "#06b6d4", "#8b5cf6", "#f59e0b",
  "#f97316", "#eab308", "#10b981", "#ef4444",
];

const fmt = (n) => (n || 0).toLocaleString("en-IN");
const fmtCur = (n) => `${activeCurrencySymbol()}${fmt(n)}`;
const convColor = (rate) => rate >= 5 ? "text-emerald-600 font-semibold" : rate >= 2 ? "text-amber-600" : "text-red-500";

/* ─── Shared sub-components ─── */

function FunnelChart({ data }) {
  if (!data?.length) return null;
  return (
    <div className="h-[200px]" data-testid="ci-funnel-chart">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} layout="vertical" margin={{ left: 10, right: 30, top: 5, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f1f5f9" />
          <XAxis type="number" tick={{ fontSize: 10 }} />
          <YAxis dataKey="stage" type="category" width={115} tick={{ fontSize: 10 }} />
          <Tooltip content={({ active, payload }) => {
            if (!active || !payload?.length) return null;
            const d = payload[0].payload;
            return <div className="bg-white border rounded-lg px-3 py-2 shadow-xl text-xs">
              <p className="font-semibold text-slate-700">{d.stage}</p>
              <p className="text-slate-500">{d.count} leads ({d.pct}%)</p>
            </div>;
          }} />
          <Bar dataKey="count" radius={[0, 4, 4, 0]}>
            {data.map((_, i) => <Cell key={i} fill={FUNNEL_COLORS[i % FUNNEL_COLORS.length]} />)}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function RecommendationCard({ rec }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className={`border rounded-lg p-3 transition-all cursor-pointer hover:shadow-sm ${PRIORITY_STYLES[rec.priority] || PRIORITY_STYLES.LOW}`}
      onClick={() => setExpanded(!expanded)} data-testid={`ci-rec-${rec.category?.toLowerCase()}`}>
      <div className="flex items-start gap-2">
        <div className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${PRIORITY_DOT[rec.priority] || PRIORITY_DOT.LOW}`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 mb-1">
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{rec.category}</span>
            <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-semibold ${rec.priority === "HIGH" ? "bg-red-100 text-red-700" : rec.priority === "MEDIUM" ? "bg-amber-100 text-amber-700" : "bg-blue-100 text-blue-700"}`}>{rec.priority}</span>
          </div>
          <p className="text-xs font-semibold text-slate-800 leading-snug">{rec.title}</p>
          {expanded && (
            <div className="mt-2 space-y-1.5">
              <p className="text-[11px] text-slate-600 leading-relaxed">{rec.insight}</p>
              <div className="flex items-start gap-1.5 mt-1">
                <ArrowRight className="w-3 h-3 text-violet-500 mt-0.5 shrink-0" />
                <p className="text-[11px] text-violet-700 font-medium">{rec.action}</p>
              </div>
              {rec.impact && <p className="text-[10px] text-slate-400 italic">{rec.impact}</p>}
            </div>
          )}
        </div>
        {expanded ? <ChevronUp className="w-3.5 h-3.5 text-slate-400 shrink-0" /> : <ChevronDown className="w-3.5 h-3.5 text-slate-400 shrink-0" />}
      </div>
    </div>
  );
}

function MiniTable({ headers, rows, testId }) {
  return (
    <div className="overflow-x-auto" data-testid={testId}>
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b border-slate-100">
            {headers.map((h, i) => <th key={i} className={`px-3 py-2 text-slate-500 font-semibold ${i === 0 ? "text-left" : "text-right"}`}>{h}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className="border-b border-slate-50 last:border-0 hover:bg-slate-50/80">
              {row.map((cell, j) => <td key={j} className={`px-3 py-2 ${j === 0 ? "font-medium text-slate-700" : "text-right text-slate-600 tabular-nums"}`}>{cell}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function KpiCard({ label, value, sub, icon: Icon, color }) {
  return (
    <div className={`border rounded-lg p-3 ${color}`}>
      <div className="flex items-center gap-1.5 mb-1">
        <Icon className="w-3.5 h-3.5 opacity-70" />
        <span className="text-[10px] font-semibold uppercase tracking-wider opacity-80">{label}</span>
      </div>
      <p className="text-lg font-bold tracking-tight tabular-nums">{value}</p>
      {sub && <p className="text-[10px] opacity-60 mt-0.5">{sub}</p>}
    </div>
  );
}

function SectionCard({ icon: Icon, iconColor, title, children }) {
  return (
    <div className="border border-slate-200 rounded-lg">
      <div className="px-4 py-2.5 border-b border-slate-100 flex items-center gap-2">
        <Icon className={`w-3.5 h-3.5 ${iconColor}`} />
        <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">{title}</span>
      </div>
      {children}
    </div>
  );
}

function DayChart({ data, label = "leads" }) {
  if (!data?.length) return null;
  const max = Math.max(...data.map(x => x[label] || x.leads || x.actions || 0), 1);
  return (
    <div className="p-4 flex items-end gap-2 justify-around h-[100px]">
      {data.map((d, i) => {
        const val = d[label] || d.leads || d.actions || 0;
        const height = Math.max((val / max) * 65, 4);
        return (
          <div key={i} className="flex flex-col items-center gap-1" title={`${d.day}: ${val}`}>
            <span className="text-[9px] text-slate-400 tabular-nums">{val}</span>
            <div className="w-9 rounded-t bg-violet-400 hover:bg-violet-500 transition-colors" style={{ height: `${height}px` }} />
            <span className="text-[10px] text-slate-500 font-medium">{d.day}</span>
          </div>
        );
      })}
    </div>
  );
}


/* ═══════════════════════════════════════════════════════════════════
   CAMPAIGN DRILL-DOWN PANEL
   ═══════════════════════════════════════════════════════════════════ */

function CampaignDrilldownPanel({ data, onClose }) {
  const m = data?.metrics;
  if (!m) return null;
  const sp = m.spend_data;

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex justify-end" onClick={onClose} data-testid="campaign-drilldown-panel">
      <div className="w-full max-w-3xl bg-white h-full overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="sticky top-0 z-10 bg-white border-b px-5 py-4 flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Target className="w-4 h-4 text-rose-500" />
              <h2 className="text-sm font-bold text-slate-800">Campaign Deep Analysis</h2>
            </div>
            <p className="text-xs text-slate-500">{m.campaign_name}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg" data-testid="campaign-drilldown-close"><X className="w-4 h-4 text-slate-400" /></button>
        </div>

        <div className="p-5 space-y-5">
          {/* KPIs Row 1: Core metrics */}
          <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
            <KpiCard icon={DollarSign} label="Amount Spent" value={fmtCur(m.amount_spent || 0)} color="border-violet-200 bg-violet-50/50 text-violet-700" />
            <KpiCard icon={BarChart3} label="Total Leads" value={fmt(m.total_leads)} color="border-blue-200 bg-blue-50/50 text-blue-700" />
            <KpiCard icon={MousePointerClick} label="CPL" value={m.cost_per_lead ? fmtCur(m.cost_per_lead) : "-"} sub="Cost per Lead" color="border-slate-200 bg-slate-50/50 text-slate-700" />
            <KpiCard icon={TrendingUp} label="Sales" value={fmt(m.total_conversions)} sub={`${m.conversion_rate}% rate`} color="border-emerald-200 bg-emerald-50/50 text-emerald-700" />
            <KpiCard icon={DollarSign} label="Revenue" value={fmtCur(m.total_revenue)} color="border-amber-200 bg-amber-50/50 text-amber-700" />
            <KpiCard icon={Target} label="CPR" value={m.cost_per_conversion ? fmtCur(m.cost_per_conversion) : "-"} sub="Cost per Sale" color="border-rose-200 bg-rose-50/50 text-rose-700" />
          </div>

          {/* Spend details */}
          {sp && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <KpiCard icon={Eye} label="Reach" value={fmt(sp.reach)} color="border-cyan-200 bg-cyan-50/50 text-cyan-700" />
              <KpiCard icon={Eye} label="Impressions" value={fmt(sp.impressions)} color="border-sky-200 bg-sky-50/50 text-sky-700" />
              <KpiCard icon={MousePointerClick} label="Clicks" value={fmt(sp.clicks)} sub={`CPC ${activeCurrencySymbol()}${sp.cpc}`} color="border-indigo-200 bg-indigo-50/50 text-indigo-700" />
              <KpiCard icon={Target} label="CTR" value={`${sp.ctr}%`} color="border-purple-200 bg-purple-50/50 text-purple-700" />
            </div>
          )}

          {/* Two-column: Funnel + Status */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <SectionCard icon={Filter} iconColor="text-blue-400" title="Lead Funnel">
              <div className="p-3"><FunnelChart data={m.funnel} /></div>
            </SectionCard>
            <SectionCard icon={BarChart3} iconColor="text-slate-400" title="Full Status Breakdown">
              <MiniTable testId="campaign-status-table" headers={["Status", "Count", "%"]}
                rows={(m.status_breakdown || []).map(s => [s.status, fmt(s.count), `${s.pct}%`])} />
            </SectionCard>
          </div>

          {/* Per-Ad breakdown within campaign */}
          {m.ad_breakdown?.length > 0 && (
            <SectionCard icon={Target} iconColor="text-orange-400" title="Individual Ads in this Campaign">
              <MiniTable testId="campaign-ads-table" headers={["Ad Name", "Ad ID", "Leads", "Conv", "Conv %"]}
                rows={m.ad_breakdown.map(a => [
                  <span className="truncate max-w-[180px] block" title={a.ad_name}>{a.ad_name?.length > 25 ? a.ad_name.slice(0, 25) + "..." : a.ad_name}</span>,
                  <span className="text-[10px] text-slate-400 font-mono">{a.ad_id?.slice(-8)}</span>,
                  fmt(a.leads), fmt(a.conversions),
                  <span className={convColor(a.conversion_rate)}>{a.conversion_rate}%</span>,
                ])} />
            </SectionCard>
          )}

          {/* City + Employee */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <SectionCard icon={MapPin} iconColor="text-blue-400" title="City Breakdown">
              <MiniTable testId="campaign-city-table" headers={["City", "Leads", "Conv", "Conv %", "Drop %"]}
                rows={(m.city_breakdown || []).map(c => [c.city, fmt(c.leads), fmt(c.conversions), <span className={convColor(c.conversion_rate)}>{c.conversion_rate}%</span>, `${c.drop_off_rate}%`])} />
            </SectionCard>
            <SectionCard icon={UserCheck} iconColor="text-emerald-400" title="Employee Handling">
              <MiniTable testId="campaign-emp-table" headers={["Employee", "Leads", "Conv", "Conv %", "Resp (min)"]}
                rows={(m.employee_breakdown || []).map(e => [e.employee, fmt(e.leads), fmt(e.conversions), <span className={convColor(e.conversion_rate)}>{e.conversion_rate}%</span>, e.avg_response_mins ?? "-"])} />
            </SectionCard>
          </div>

          {/* Drop-off + Aging */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <SectionCard icon={AlertTriangle} iconColor="text-red-400" title="Drop-off Reasons">
              <MiniTable testId="campaign-drop-table" headers={["Reason", "Count", "%"]}
                rows={(m.drop_off_reasons || []).map(d => [d.reason, fmt(d.count), `${d.pct}%`])} />
            </SectionCard>
            <SectionCard icon={Clock} iconColor="text-indigo-400" title="Lead Aging">
              <MiniTable testId="campaign-aging-table" headers={["Bucket", "Count"]}
                rows={(m.lead_aging || []).map(a => [a.bucket, fmt(a.count)])} />
            </SectionCard>
          </div>

          {/* Transitions + Daily */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {m.top_transitions?.length > 0 && (
              <SectionCard icon={ArrowRight} iconColor="text-purple-400" title="Status Transitions">
                <MiniTable testId="campaign-trans-table" headers={["Transition", "Count"]}
                  rows={m.top_transitions.map(t => [t.transition, fmt(t.count)])} />
              </SectionCard>
            )}
            {m.daily_patterns?.length > 0 && (
              <SectionCard icon={Clock} iconColor="text-violet-400" title="Leads by Day of Week">
                <DayChart data={m.daily_patterns} />
              </SectionCard>
            )}
          </div>

          {/* AI Recommendations */}
          <SectionCard icon={Sparkles} iconColor="text-violet-400" title={`AI Recommendations for "${m.campaign_name}" (${data.recommendations?.length || 0})`}>
            <div className="p-3 space-y-2">
              {data.recommendations?.map((rec, i) => <RecommendationCard key={i} rec={rec} />)}
            </div>
          </SectionCard>
        </div>
      </div>
    </div>
  );
}


/* ═══════════════════════════════════════════════════════════════════
   EMPLOYEE DRILL-DOWN PANEL
   ═══════════════════════════════════════════════════════════════════ */

function EmployeeDrilldownPanel({ data, onClose }) {
  const m = data?.metrics;
  if (!m) return null;
  const rs = m.response_time_stats;

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex justify-end" onClick={onClose} data-testid="emp-drilldown-panel">
      <div className="w-full max-w-3xl bg-white h-full overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="sticky top-0 z-10 bg-white border-b px-5 py-4 flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <UserCheck className="w-4 h-4 text-emerald-500" />
              <h2 className="text-sm font-bold text-slate-800">Employee Deep Analysis</h2>
            </div>
            <p className="text-xs text-slate-500">{m.employee}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg" data-testid="emp-drilldown-close"><X className="w-4 h-4 text-slate-400" /></button>
        </div>

        <div className="p-5 space-y-5">
          {/* KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <KpiCard icon={BarChart3} label="Leads" value={fmt(m.total_leads)} color="border-blue-200 bg-blue-50/50 text-blue-700" />
            <KpiCard icon={TrendingUp} label="Conversions" value={fmt(m.total_conversions)} sub={`${m.conversion_rate}% rate`} color="border-emerald-200 bg-emerald-50/50 text-emerald-700" />
            <KpiCard icon={AlertTriangle} label="Dropped" value={fmt(m.total_dropped)} sub={`${m.drop_off_rate}% rate`} color="border-red-200 bg-red-50/50 text-red-700" />
            <KpiCard icon={DollarSign} label="Revenue" value={fmtCur(m.total_revenue)} sub={`${fmt(m.total_activities)} activities`} color="border-amber-200 bg-amber-50/50 text-amber-700" />
          </div>

          {/* Response Time Stats */}
          {rs && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <KpiCard icon={Zap} label="Fastest" value={`${rs.min_mins}m`} color="border-emerald-200 bg-emerald-50/50 text-emerald-700" />
                <KpiCard icon={Clock} label="Median" value={`${rs.median_mins}m`} sub={rs.median_mins > 60 ? `= ${Math.round(rs.median_mins / 60)}h ${rs.median_mins % 60}m` : null} color="border-amber-200 bg-amber-50/50 text-amber-700" />
                <KpiCard icon={Clock} label="P95" value={`${rs.p95_mins}m`} sub={rs.p95_mins > 60 ? `= ${Math.round(rs.p95_mins / 60)}h ${rs.p95_mins % 60}m` : null} color="border-rose-200 bg-rose-50/50 text-rose-700" />
                <KpiCard icon={BarChart3} label="Sample Size" value={fmt(rs.count)} sub="leads measured" color="border-slate-200 bg-slate-50/50 text-slate-700" />
              </div>

              {/* Response Time Distribution */}
              <SectionCard icon={Clock} iconColor="text-orange-400" title="Response Time Distribution">
                <div className="p-4 flex items-end gap-2 justify-around h-[110px]">
                  {(rs.distribution || []).map((b, i) => {
                    const max = Math.max(...(rs.distribution || []).map(x => x.count), 1);
                    const height = Math.max((b.count / max) * 70, 4);
                    const isGood = i < 3;
                    return (
                      <div key={i} className="flex flex-col items-center gap-1" title={`${b.bucket}: ${b.count} leads`}>
                        <span className="text-[9px] text-slate-400 tabular-nums">{b.count}</span>
                        <div className={`w-10 rounded-t transition-colors ${isGood ? "bg-emerald-400 hover:bg-emerald-500" : "bg-red-400 hover:bg-red-500"}`} style={{ height: `${height}px` }} />
                        <span className="text-[9px] text-slate-500 font-medium whitespace-nowrap">{b.bucket}</span>
                      </div>
                    );
                  })}
                </div>
              </SectionCard>
            </>
          )}

          {/* Two-column: Funnel + Status */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <SectionCard icon={Filter} iconColor="text-blue-400" title="Lead Funnel">
              <div className="p-3"><FunnelChart data={m.funnel} /></div>
            </SectionCard>
            <SectionCard icon={BarChart3} iconColor="text-slate-400" title="Full Status Breakdown">
              <MiniTable testId="emp-status-table" headers={["Status", "Count", "%"]}
                rows={(m.status_breakdown || []).map(s => [s.status, fmt(s.count), `${s.pct}%`])} />
            </SectionCard>
          </div>

          {/* Ad performance + City */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <SectionCard icon={Target} iconColor="text-rose-400" title="Ad Performance (Which ads they handle best)">
              <MiniTable testId="emp-ad-table" headers={["Ad", "Leads", "Conv", "Conv %", "Drop %"]}
                rows={(m.ad_breakdown || []).map(a => [
                  <span className="truncate max-w-[150px] block" title={a.ad_name}>{a.ad_name?.length > 20 ? a.ad_name.slice(0, 20) + "..." : a.ad_name}</span>,
                  fmt(a.leads), fmt(a.conversions),
                  <span className={convColor(a.conversion_rate)}>{a.conversion_rate}%</span>,
                  `${a.drop_off_rate}%`
                ])} />
            </SectionCard>
            <SectionCard icon={MapPin} iconColor="text-blue-400" title="City Performance">
              <MiniTable testId="emp-city-table" headers={["City", "Leads", "Conv", "Conv %", "Drop %"]}
                rows={(m.city_breakdown || []).map(c => [c.city, fmt(c.leads), fmt(c.conversions), <span className={convColor(c.conversion_rate)}>{c.conversion_rate}%</span>, `${c.drop_off_rate}%`])} />
            </SectionCard>
          </div>

          {/* Drop-off + Aging */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <SectionCard icon={AlertTriangle} iconColor="text-red-400" title="Drop-off Reasons">
              <MiniTable testId="emp-drop-table" headers={["Reason", "Count", "%"]}
                rows={(m.drop_off_reasons || []).map(d => [d.reason, fmt(d.count), `${d.pct}%`])} />
            </SectionCard>
            <SectionCard icon={Clock} iconColor="text-indigo-400" title="Lead Aging (Active Leads)">
              <MiniTable testId="emp-aging-table" headers={["Bucket", "Count"]}
                rows={(m.lead_aging || []).map(a => [a.bucket, fmt(a.count)])} />
            </SectionCard>
          </div>

          {/* Stale Leads */}
          {m.stale_leads?.length > 0 && (
            <SectionCard icon={AlertTriangle} iconColor="text-orange-400" title={`Stale Leads (${m.stale_leads.length} leads inactive 14+ days)`}>
              <MiniTable testId="emp-stale-table" headers={["Lead ID", "Status", "City", "Days Stale"]}
                rows={m.stale_leads.map(s => [
                  <span className="text-[10px] font-mono">{s.id?.slice(0, 8)}...</span>,
                  s.status, s.city || "-", <span className="text-red-500 font-semibold">{s.days_stale}</span>
                ])} />
            </SectionCard>
          )}

          {/* Activity by day + Transitions */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {m.daily_activity?.length > 0 && (
              <SectionCard icon={Clock} iconColor="text-violet-400" title="Activity by Day of Week">
                <DayChart data={m.daily_activity} label="actions" />
              </SectionCard>
            )}
            {m.top_transitions?.length > 0 && (
              <SectionCard icon={ArrowRight} iconColor="text-purple-400" title="Status Transitions">
                <MiniTable testId="emp-trans-table" headers={["Transition", "Count"]}
                  rows={m.top_transitions.map(t => [t.transition, fmt(t.count)])} />
              </SectionCard>
            )}
          </div>

          {/* AI Recommendations */}
          <SectionCard icon={Sparkles} iconColor="text-violet-400" title={`AI Coaching for ${m.employee} (${data.recommendations?.length || 0})`}>
            <div className="p-3 space-y-2">
              {data.recommendations?.map((rec, i) => <RecommendationCard key={i} rec={rec} />)}
            </div>
          </SectionCard>
        </div>
      </div>
    </div>
  );
}


/* ═══════════════════════════════════════════════════════════════════
   MAIN CONVERSION INTELLIGENCE COMPONENT
   ═══════════════════════════════════════════════════════════════════ */

export default function ConversionIntelligence() {
  const [data, setCiData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [period, setPeriod] = useState("last_30_days");
  const [expanded, setExpanded] = useState(true);

  // Drill-down state
  const [campDrill, setCampDrill] = useState(null);
  const [empDrill, setEmpDrill] = useState(null);

  const fetchCI = async () => {
    setLoading(true);
    try {
      const res = await analyticsApi.getConversionIntelligence({ period });
      if (res.data.success) setCiData(res.data);
      else toast.error("Failed to load conversion intelligence");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load conversion intelligence");
    } finally { setLoading(false); }
  };

  const openCampaignDrill = async (campaignId) => {
    setCampDrill({ loading: true, data: null });
    try {
      const res = await analyticsApi.getCampaignDrilldown({ campaign_id: campaignId, period });
      setCampDrill({ loading: false, data: res.data });
    } catch (err) {
      toast.error("Failed to load campaign analytics");
      setCampDrill(null);
    }
  };

  const openEmpDrill = async (empId) => {
    setEmpDrill({ loading: true, data: null });
    try {
      const res = await analyticsApi.getEmployeeDrilldown({ employee_id: empId, period });
      setEmpDrill({ loading: false, data: res.data });
    } catch (err) {
      toast.error("Failed to load employee analytics");
      setEmpDrill(null);
    }
  };

  const m = data?.metrics;

  return (
    <div className="bg-white rounded-xl border border-slate-200/80 overflow-hidden" data-testid="conversion-intelligence">
      {/* Header */}
      <div className="px-5 py-3.5 border-b border-slate-100 flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2.5">
          <div className="p-1.5 bg-violet-100 rounded-md"><Brain className="w-4 h-4 text-violet-600" strokeWidth={2} /></div>
          <div>
            <h3 className="text-sm font-semibold text-slate-800 tracking-tight">Conversion Intelligence</h3>
            <p className="text-[11px] text-slate-400">Click any ad or employee row to drill down into deep analytics</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-0.5 bg-slate-100 rounded-lg p-0.5" data-testid="ci-period-select">
            {PERIOD_OPTIONS.map(p => (
              <button key={p.value} onClick={() => setPeriod(p.value)}
                className={`px-2.5 py-1.5 text-[11px] font-medium rounded-md transition-colors whitespace-nowrap ${period === p.value ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
                data-testid={`ci-period-${p.value}`}>{p.label}</button>
            ))}
          </div>
          <button onClick={fetchCI} disabled={loading}
            className="flex items-center gap-1.5 px-3 py-2 bg-violet-600 hover:bg-violet-700 text-white rounded-lg transition-colors disabled:opacity-50 shadow-sm font-medium text-xs"
            data-testid="run-ci-btn">
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Brain className="w-3.5 h-3.5" />}
            {loading ? "Analyzing..." : "Run Analysis"}
          </button>
          {data && (
            <button onClick={() => setExpanded(!expanded)} className="p-1.5 hover:bg-slate-100 rounded-md" data-testid="ci-toggle">
              {expanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
            </button>
          )}
        </div>
      </div>

      {/* Empty State */}
      {!data && !loading && (
        <div className="px-5 py-10 text-center">
          <Brain className="w-10 h-10 text-violet-200 mx-auto mb-3" />
          <p className="text-sm text-slate-400 mb-1">Click "Run Analysis" to analyze your lead conversion data</p>
          <p className="text-[11px] text-slate-300">Deep analytics per ad, per employee, funnel drop-offs, and AI recommendations</p>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="px-5 py-12 text-center">
          <Loader2 className="w-8 h-8 text-violet-400 animate-spin mx-auto mb-3" />
          <p className="text-xs text-slate-500">Crunching lead data & generating AI recommendations...</p>
        </div>
      )}

      {/* Results */}
      {data && expanded && m && (
        <div className="p-5 space-y-5" data-testid="ci-results">
          {/* KPI Row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <KpiCard icon={BarChart3} label="Total Leads" value={fmt(m.total_leads)} color="border-blue-200 bg-blue-50/50 text-blue-700" />
            <KpiCard icon={TrendingUp} label="Conversions" value={fmt(m.total_conversions)} color="border-emerald-200 bg-emerald-50/50 text-emerald-700" />
            <KpiCard icon={Target} label="Conv. Rate" value={`${m.overall_conversion_rate}%`} color="border-amber-200 bg-amber-50/50 text-amber-700" />
            <KpiCard icon={Clock} label="Period" value={period.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())} sub={`Generated ${new Date(data.generated_at).toLocaleTimeString()}`} color="border-violet-200 bg-violet-50/50 text-violet-700" />
          </div>

          {/* Two-column: Funnel + Recommendations */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <SectionCard icon={Filter} iconColor="text-blue-400" title="Lead Funnel">
              <div className="p-3"><FunnelChart data={m.funnel} /></div>
            </SectionCard>
            <SectionCard icon={Sparkles} iconColor="text-violet-400" title="AI Recommendations">
              <div className="p-3 space-y-2 max-h-[280px] overflow-y-auto">
                {data.recommendations?.length > 0 ? data.recommendations.map((rec, i) => <RecommendationCard key={i} rec={rec} />) : <p className="text-xs text-slate-400 text-center py-4">No recommendations</p>}
              </div>
            </SectionCard>
          </div>

          {/* ═══ CLICKABLE CAMPAIGN TABLE ═══ */}
          <SectionCard icon={Target} iconColor="text-rose-400" title="Campaign Performance (Click any row to drill down)">
            <div className="overflow-x-auto" data-testid="ci-campaigns-table">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-slate-100">
                    {["Campaign", "Amt Spent", "Leads", "CPL", "Sales", "Revenue", "CPR"].map((h, i) => (
                      <th key={i} className={`px-3 py-2 text-slate-500 font-semibold ${i === 0 ? "text-left" : "text-right"}`}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(m.campaign_performance || []).map((c, i) => (
                    <tr key={i} className="border-b border-slate-50 last:border-0 hover:bg-violet-50 cursor-pointer transition-colors group"
                      onClick={() => openCampaignDrill(c.campaign_id)} data-testid={`campaign-row-${i}`}>
                      <td className="px-3 py-2.5 font-medium text-slate-700 group-hover:text-violet-700">
                        <span className="truncate max-w-[200px] block" title={c.campaign_name}>{c.campaign_name}</span>
                      </td>
                      <td className="px-3 py-2.5 text-right text-slate-600 tabular-nums">{fmtCur(c.amount_spent)}</td>
                      <td className="px-3 py-2.5 text-right text-slate-600 tabular-nums">{fmt(c.leads)}</td>
                      <td className="px-3 py-2.5 text-right text-slate-600 tabular-nums">{c.cpl ? fmtCur(c.cpl) : "-"}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums"><span className={convColor(c.conversion_rate)}>{fmt(c.sales)}</span></td>
                      <td className="px-3 py-2.5 text-right text-slate-600 tabular-nums">{fmtCur(c.revenue)}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums font-semibold">{c.cpr ? fmtCur(c.cpr) : "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </SectionCard>

          {/* ═══ CLICKABLE EMPLOYEE TABLE ═══ */}
          <SectionCard icon={UserCheck} iconColor="text-emerald-400" title="Employee Performance (Click any row to drill down)">
            <div className="overflow-x-auto" data-testid="ci-employee-table">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-slate-100">
                    {["Employee", "Leads", "Conv", "Conv %", "Drop %", "Resp (min)"].map((h, i) => (
                      <th key={i} className={`px-3 py-2 text-slate-500 font-semibold ${i === 0 ? "text-left" : "text-right"}`}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(m.employee_metrics || []).map((e, i) => (
                    <tr key={i} className="border-b border-slate-50 last:border-0 hover:bg-emerald-50 cursor-pointer transition-colors group"
                      onClick={() => openEmpDrill(e.employee_id)} data-testid={`emp-row-${i}`}>
                      <td className="px-3 py-2.5 font-medium text-slate-700 group-hover:text-emerald-700">{e.employee}</td>
                      <td className="px-3 py-2.5 text-right text-slate-600 tabular-nums">{fmt(e.leads)}</td>
                      <td className="px-3 py-2.5 text-right text-slate-600 tabular-nums">{fmt(e.conversions)}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums"><span className={convColor(e.conversion_rate)}>{e.conversion_rate}%</span></td>
                      <td className="px-3 py-2.5 text-right text-slate-500 tabular-nums">{e.drop_off_rate}%</td>
                      <td className="px-3 py-2.5 text-right text-slate-500 tabular-nums">{e.avg_response_mins ?? "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </SectionCard>

          {/* City + Source + Day */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <SectionCard icon={MapPin} iconColor="text-blue-400" title="City Patterns">
              <MiniTable testId="ci-city-table" headers={["City", "Leads", "Conv", "Conv %", "Drop %"]}
                rows={(m.city_patterns || []).map(c => [c.city, fmt(c.leads), fmt(c.conversions), <span className={convColor(c.conversion_rate)}>{c.conversion_rate}%</span>, `${c.drop_off_rate}%`])} />
            </SectionCard>
            <SectionCard icon={Target} iconColor="text-amber-400" title="Source Analysis">
              <MiniTable testId="ci-source-table" headers={["Source", "Leads", "Conv %"]}
                rows={(m.source_analysis || []).map(s => [s.source?.replace(/_/g, " "), fmt(s.leads), <span className={convColor(s.conversion_rate)}>{s.conversion_rate}%</span>])} />
            </SectionCard>
          </div>

          {m.daily_patterns?.length > 0 && (
            <SectionCard icon={Clock} iconColor="text-violet-400" title="Conversion by Day of Week">
              <DayChart data={m.daily_patterns} />
            </SectionCard>
          )}
        </div>
      )}

      {/* Collapsed summary */}
      {data && !expanded && (
        <div className="px-5 py-2.5 flex items-center gap-4 text-[11px] text-slate-400">
          <span>{fmt(m?.total_leads)} leads</span>
          <span>{m?.overall_conversion_rate}% conv</span>
          <span>{data.recommendations?.length || 0} recommendations</span>
          <span className="ml-auto">Click arrow to expand</span>
        </div>
      )}

      {/* ═══ DRILL-DOWN PANELS ═══ */}
      {campDrill?.loading && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center">
          <div className="bg-white rounded-xl p-8 text-center shadow-2xl">
            <Loader2 className="w-8 h-8 text-violet-400 animate-spin mx-auto mb-3" />
            <p className="text-sm text-slate-600">Loading deep campaign analytics...</p>
            <p className="text-[11px] text-slate-400 mt-1">Analyzing funnel, cities, employees, spend & generating AI recommendations</p>
          </div>
        </div>
      )}
      {campDrill?.data && <CampaignDrilldownPanel data={campDrill.data} onClose={() => setCampDrill(null)} />}

      {empDrill?.loading && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center">
          <div className="bg-white rounded-xl p-8 text-center shadow-2xl">
            <Loader2 className="w-8 h-8 text-emerald-400 animate-spin mx-auto mb-3" />
            <p className="text-sm text-slate-600">Loading deep employee analytics...</p>
            <p className="text-[11px] text-slate-400 mt-1">Analyzing response times, funnel, ads & generating AI coaching</p>
          </div>
        </div>
      )}
      {empDrill?.data && <EmployeeDrilldownPanel data={empDrill.data} onClose={() => setEmpDrill(null)} />}
    </div>
  );
}
import { activeCurrencySymbol } from '@/utils/currency';
